/**
 * The page is imported from **its own module**, not from the `@pages` barrel — the barrel pulls
 * every screen into one shared chunk and defeats the route-level code splitting (EPIC-003).
 */
import { createFileRoute } from '@tanstack/react-router';

import { AdminExternalCallsPage } from '@pages/admin-external-calls';
import { ExternalCallLogModel } from '@units/external-call-log';
import { IamService } from '@units/iam';

/**
 * `/admin/external-calls` — wiring only (`rules/frontend-fsd.mdc` rule 10).
 *
 * The guard is `external_call_log:read`, which is what `GET /ai/external-calls` declares — and it is
 * deliberately **not** `ai_provider:read`: reading what left the installation and being allowed to
 * see how the providers are configured are two different jobs, and an attestation reviewer holds the
 * first without the second.
 *
 * The search schema is the screen's state, validated here so that a hand-edited URL cannot reach a
 * component: an unknown outcome, a page that is a word, a page size beyond what the operation
 * accepts — each falls back to its default inside the schema rather than replacing a journal with an
 * error boundary. The server does the opposite and answers 422, because there an unknown value means
 * a client sent it.
 *
 * **No loader**, so the route inherits the router's pending and error boundaries rather than
 * declaring its own; the screen renders its own states around one query.
 */
export const Route = createFileRoute('/_authenticated/admin/external-calls')({
  beforeLoad: IamService.IamGuards.requirePermission({
    permission: 'external_call_log:read',
    whenDenied: 'forbidden',
  }),
  validateSearch: ExternalCallLogModel.externalCallSearchSchema,
  component: AdminExternalCallsPage,
  staticData: { crumbKey: 'ai.externalCalls.title' },
});
